/**
 * SignalR connection configuration
 */

import { tryGetPlatformConfig } from "@/platform/platform-context";

function readEnvHubUrl(): string | undefined {
  const viteUrl = import.meta.env.VITE_HUB_URL;
  if (viteUrl) return viteUrl;
  // `process` is undefined in the browser (Vite). Only touch it where it actually exists
  // (Node / SSR / Next builds) so the browser bundle never throws "Can't find variable: process".
  if (typeof process !== "undefined" && process.env) {
    return process.env.VITE_HUB_URL || process.env.NEXT_PUBLIC_HUB_URL;
  }
  return undefined;
}

export function getHubUrl(): string {
  const fromPlatform = tryGetPlatformConfig()?.hubUrl;
  if (fromPlatform) return fromPlatform;
  return readEnvHubUrl() ?? "";
}

export const SIGNALR_CONFIG = {
  get HUB_URL() {
    return getHubUrl();
  },
  RETRY_DELAY_MS: 5000,
  MAX_RETRY_ATTEMPTS: 5,
  CONNECTION_TIMEOUT_MS: 30000,
  // SERVER_TIMEOUT_MS must be comfortably MORE than the server's KeepAliveInterval, which is 15s
  // on every gateway (Vybe.SignalR.Server.GatewayHost, and VybeGateway.SignalR.Vybe's Program.cs).
  //
  // It used to be 15000 — exactly the server's ping period, so there was zero margin and any jitter
  // read as a dead link. Reproduced in the real terminal: 150 rapid chart switches across a 4-chart
  // grid produced "OHLC request timed out with no data", then
  //   Connection disconnected with error 'Server timeout elapsed without receiving a message'
  //   HubConnection reconnected successfully
  //   Connection disconnected.                        <- straight back down
  // and the charts were left blank because the post-reconnect backfill threw "Not connected".
  // The UI never janked (0 long tasks, steady 60fps throughout) — this was never a rendering
  // problem, it was the client hanging up on a healthy server.
  //
  // SignalR's guidance is at least double the server keepalive; 40s gives real headroom on top.
  // A genuinely dead socket is still caught here, and the wake watchdog handles the sleep/resume
  // case far faster than this fallback ever did.
  SERVER_TIMEOUT_MS: 40000,
  KEEPALIVE_MS: 7000,
} as const;

export const AUTH_HEADERS = {
  VYBE_LOGIN: "X-Vybe-Login",
  VYBE_PASSWORD: "X-Vybe-Password",
  VYBE_HOST: "X-Vybe-Host",
  VYBE_PORT: "X-Vybe-Port",
  TRADING_PROVIDER: "X-Trading-Provider",
  /** Single-connection auth (MT5 parity): the CRM session token; the GATEWAY resolves credentials. */
  VYBE_TOKEN: "X-Vybe-Token",
} as const;

export const CONNECTION_STATUS = {
  DISCONNECTED: "disconnected",
  CONNECTING: "connecting",
  CONNECTED: "connected",
  RECONNECTING: "reconnecting",
} as const;

export enum TradingProvider {
  /** MetaTrader 5 (T5) */
  MT5 = 0,
  /** MetaTrader 4 */
  MT4 = 1,
  /** Vybe trading engine */
  Vybe = 2,
  /** Custom trading provider */
  Custom = 99,
}

export type ConnectionStatus =
  (typeof CONNECTION_STATUS)[keyof typeof CONNECTION_STATUS];

export interface ConnectionCredentials {
  login: number;
  password: string;
  host: string;
  port: number;
  provider?: TradingProvider;
  /** Per-account gateway SignalR base URL. Falls back to SIGNALR_CONFIG.HUB_URL when absent. */
  hubUrl?: string;
}
